import type { TFunction } from 'i18next'
import type { ExtraPackageContentItem } from '../api/types'
import { partitionPackageContents } from './packageContents'
import {
  formatMultiplierNumber,
  getCardMeta,
  type PackageCardMeta,
  type PackageContentMeta,
  type PackageMarketingMetadata,
} from './packCardMeta'

/**
 * Resolve the V3 popup chip text from a pack's card metadata. Priority:
 *   1. equivalentImagesTextI18nKey (translated, with `count` interpolation)
 *   2. equivalentImagesText (plain literal)
 *   3. equivalentImages (raw count → default i18n template "≈ {{count}}x images")
 *   4. null (caller renders a placeholder)
 *
 * Empty strings collapse to `null` so consumers can branch on a single
 * nullish check.
 */
export function resolveEquivalentImagesText(
  cardMeta: PackageCardMeta,
  t: TFunction,
): string | null {
  const count = cardMeta.equivalentImages
  const fallback = cardMeta.equivalentImagesText ?? null
  const i18nKey = cardMeta.equivalentImagesTextI18nKey

  if (i18nKey) {
    const translated = t(i18nKey, { count, defaultValue: fallback ?? '' })
    return translated || fallback || null
  }
  if (fallback) return fallback
  if (count != null) {
    return t('membership:extra-pack.card.equivalent-images-default', {
      count,
      defaultValue: '≈ {{count}}x images',
    })
  }
  return null
}

/**
 * View-model row used by OneTimePackCard. Defined in the domain layer so the
 * helpers below can be typed without importing from the component layer
 * (avoids a circular dependency).
 */
export type OneTimePackCardRow = {
  label: string
  value: string
  total?: boolean
}

/** Surfaced separately from `rows` so the card can resolve the label async via `useDecoration(code)`. */
export type OneTimePackCardDecoration = {
  code: string
  quantity: number
}

/**
 * View-model produced by `getOneTimeCardDisplayData` and consumed by
 * `OneTimePackCard`.
 */
export type OneTimePackCardDisplayData = {
  title: string
  multiplier: string | null
  rows: OneTimePackCardRow[]
  decorations: OneTimePackCardDecoration[]
  equivalentImagesText: string | null
  /** True when packageContents include at least one kaisuuken item — drives the card-icon overlay on V3. */
  hasKaisuuken: boolean
}

export function getOneTimeCardDisplayData({
  marketingMetadata,
  packageContents,
  t,
}: {
  marketingMetadata?: Record<string, unknown> | null
  packageContents: ExtraPackageContentItem[]
  t: TFunction
}): OneTimePackCardDisplayData {
  const cardMeta = getCardMeta(
    marketingMetadata as PackageMarketingMetadata | null,
  )
  const resolvedPackName = cardMeta.packName ?? inferPackName()
  const title = t(cardMeta.packNameI18nKey ?? '', {
    defaultValue: resolvedPackName,
  })

  const valueRatioPercent = cardMeta.valueRatioPercent
  const multiplier =
    valueRatioPercent && valueRatioPercent > 0
      ? formatMultiplierNumber(valueRatioPercent)
      : null

  const { regular, decorations } = partitionPackageContents(packageContents)
  const equivalentImagesText = resolveEquivalentImagesText(cardMeta, t)
  const hasKaisuuken = regular.some(item => item.kind === 'kaisuuken')
  return {
    title,
    multiplier,
    rows: getOneTimeFeatureRows(regular, cardMeta, t),
    decorations,
    equivalentImagesText,
    hasKaisuuken,
  }
}

function getOneTimeFeatureRows(
  packageContents: ExtraPackageContentItem[],
  cardMeta: ReturnType<typeof getCardMeta>,
  t: TFunction,
): OneTimePackCardRow[] {
  const visibleContents = packageContents.filter(content => {
    const metadata = (content.metadata ?? {}) as PackageContentMeta
    return metadata.hideInCard !== true
  })

  // The card's big headline number already says the credits total, so the
  // leading 'Credits XK' row would be redundant — only extras and the
  // total-value row appear here.
  const baseRows: OneTimePackCardRow[] = []

  for (const content of visibleContents) {
    const metadata = (content.metadata ?? {}) as PackageContentMeta
    const labelFallback =
      metadata.displayName ??
      metadata.label ??
      metadata.name ??
      formatPackageContentLabel(content)
    const label = t(metadata.displayNameI18nKey ?? '', {
      defaultValue: labelFallback,
    })
    const value = `x${content.quantity ?? 0}`

    baseRows.push({
      label,
      value,
    })
  }

  const totalValueFallback = cardMeta.totalValueText
  if (totalValueFallback) {
    const totalValue = t(cardMeta.totalValueTextI18nKey ?? '', {
      defaultValue: totalValueFallback,
    })
    const totalLabel = t('membership:extra-pack.card.total-value-label', {
      defaultValue: 'Total Value',
    })

    baseRows.push({
      label: totalLabel,
      value: totalValue,
      total: true,
    })
  }

  // Keep all configured rows visible. If package contents grow much larger in
  // the future, revisit this because the card can become too tall.
  return baseRows
}

function inferPackName() {
  return 'Credit Pack'
}

function formatPackageContentLabel(item: ExtraPackageContentItem) {
  const title = item.kind === 'kaisuuken' ? 'Kaisuuken' : item.kind
  return title
}
